import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ROLE_LABELS, isAdmin, isAdminOrGestor } from '../lib/rbac';
import { CupIcon } from './CupIcon';
import { NotificationBell } from './NotificationBell';
import { MaintenanceBanner } from './MaintenanceBanner';
import { APP_VERSION } from '../lib/version';

export function Layout() {
  const { fullName, email, orgs, currentOrgId, setCurrentOrgId, grants, signOut } = useAuth();
  const org = orgs.find((o) => o.orgId === currentOrgId);
  const admin = grants ? isAdmin(grants) : false;
  const pmo = grants ? isAdminOrGestor(grants) : false;
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Fecha o menu (celular) ao trocar de tela ou ao apertar Esc.
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className={`app${menuOpen ? ' menu-open' : ''}`}>
      <header className="mobile-bar no-print">
        <button
          className="menu-toggle"
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
        </button>
        <div className="mark"><CupIcon /></div>
        <strong className="mobile-title">Workflow de Novos Produtos</strong>
      </header>
      <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} />
      <aside className="sidebar">
        <div className="sidebar-head">
          <div className="brand-logos">
            <img
              className="brand-logo"
              src="/logo-trevo.png"
              alt="Trevo Lácteos"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <img
              className="brand-logo"
              src="/logo-ehrmann.png"
              alt="Ehrmann"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div className="brand-row">
            <div className="mark"><CupIcon /></div>
            <div className="brandline">
              <strong>Workflow de Novos Produtos</strong>
              <span>{org?.orgName ?? '—'}</span>
            </div>
          </div>
        </div>

        {orgs.length > 1 && (
          <div style={{ padding: '10px 12px 0' }}>
            <select
              value={currentOrgId ?? ''}
              onChange={(e) => setCurrentOrgId(e.target.value)}
              style={{ width: '100%' }}
            >
              {orgs.map((o) => (
                <option key={o.orgId} value={o.orgId}>
                  {o.orgName}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="nav">
          <NavLink to="/" end>
            Painel de Lançamentos
          </NavLink>
          {pmo && <NavLink to="/executivo">Painel Executivo</NavLink>}
          <NavLink to="/tarefas">Minhas tarefas</NavLink>
          {pmo && <NavLink to="/equipe">Equipe</NavLink>}
          <NavLink to="/atrasos">Painel de atrasos</NavLink>
          <NavLink to="/reunioes">Reuniões</NavLink>
          <NavLink to="/outros-assuntos">Outros assuntos</NavLink>
          <NavLink to="/cancelados">Cancelados</NavLink>
          <NavLink to="/rampa">Rampa de lançamentos</NavLink>
          <NavLink to="/saida-linha">Saída de linha</NavLink>
          {admin && <NavLink to="/usuarios">Usuários</NavLink>}
          {admin && <NavLink to="/adesao">Adesão de uso</NavLink>}
          {admin && <NavLink to="/etapas">Etapas &amp; responsáveis</NavLink>}
          <NavLink to="/metodologia">Como funciona</NavLink>
        </nav>

        <div className="sidebar-foot">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {fullName ?? email}
            </div>
            <NotificationBell />
          </div>
          <div className="muted">{grants ? ROLE_LABELS[grants.role] : ''}</div>
          <div className="muted" style={{ fontSize: 10.5, marginTop: 6 }}>
            Seus acessos e tempo de uso são registrados para acompanhar a adesão à ferramenta.
          </div>
          <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }} title="Versão publicada">
            {APP_VERSION}
          </div>
          <button className="btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => signOut()}>
            Sair
          </button>
        </div>
      </aside>

      <div className="main">
        <MaintenanceBanner />
        <Outlet />
      </div>
    </div>
  );
}
